import { Dispatch, SetStateAction, useMemo } from 'react';

import InputDateRange from '@atom/components/common/inputDateRange/InputDateRange';
import UsersFilter from '@atom/components/common/usersFilter/UsersFilter';
import { Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  AssetRequestsFilters,
  DescriptionOption,
} from '@atom/types/assetRequest';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import {
  convertDateToLocalDayEnd,
  convertDateToLocalDayStart,
} from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import {
  DateRange,
  DateRangeTerminator,
} from '@atom/utilities/workOrdersDateFilterUtilities';

import {
  DESCRIPTION_LABELS,
  DESCRIPTION_OPTIONS,
} from '../assetRequestConstants';

import classes from './sddotFilters.module.css';

const { MenuItem } = Select;

const styles = {
  input: {
    marginTop: '1rem',
  },
  label: {
    color: colors.neutral.dim,
  },
};

interface Props {
  filtersCart: AssetRequestsFilters;
  setFiltersCart: Dispatch<SetStateAction<AssetRequestsFilters>>;
  asset?: InventoryAssetDetailType;
  createdPresetSelected?: DateRange;
  setCreatedPresetSelect?: Dispatch<SetStateAction<DateRange>>;
}

const SDDOTStockpileFilters = ({
  filtersCart,
  setFiltersCart,
  asset,
  createdPresetSelected,
  setCreatedPresetSelect,
}: Props) => {
  const updateFilter = (value: any, property: keyof AssetRequestsFilters) => {
    setFiltersCart(prev => ({ ...prev, [property]: value }));
  };

  const getDateValue = (property: keyof AssetRequestsFilters) => {
    if (!isNilOrEmpty(filtersCart[property])) {
      return new Date(Number(filtersCart[property]));
    }

    return null;
  };

  const handleDateChange =
    (property: keyof AssetRequestsFilters, terminator: DateRangeTerminator) =>
    (val: Date) => {
      const dateMillis = val
        ? terminator === DateRangeTerminator.START
          ? convertDateToLocalDayStart(val)
          : convertDateToLocalDayEnd(val)
        : null;
      updateFilter(dateMillis, property);
    };

  const handleDescriptionUpdate = event => {
    const option = event.target.value;

    const updatedFilters = {
      [DescriptionOption.ALL]: {
        assetId: asset.id,
        fromAssetId: asset.id,
      },
      [DescriptionOption.OUTGOING]: {
        assetId: asset.id,
        fromAssetId: null,
      },
      [DescriptionOption.INCOMING]: {
        assetId: null,
        fromAssetId: asset.id,
      },
    };

    setFiltersCart(prev => ({ ...prev, ...updatedFilters[option] }));
  };

  const descriptionValue = useMemo(() => {
    switch (true) {
      case !isNilOrEmpty(filtersCart.assetId) &&
        !isNilOrEmpty(filtersCart.fromAssetId): {
        return DescriptionOption.ALL;
      }
      case !isNilOrEmpty(filtersCart.assetId) &&
        isNilOrEmpty(filtersCart.fromAssetId): {
        return DescriptionOption.OUTGOING;
      }
      case isNilOrEmpty(filtersCart.assetId) &&
        !isNilOrEmpty(filtersCart.fromAssetId): {
        return DescriptionOption.INCOMING;
      }
      default:
        return '';
    }
  }, [filtersCart.assetId, filtersCart.fromAssetId]);

  const showDescriptionFilter = !isNilOrEmpty(asset);

  return (
    <>
      <div className={classes.inputContainer}>
        <TextField
          style={styles.input}
          slotProps={{ inputLabel: { style: styles.label } }}
          type="number"
          value={Number(filtersCart.name)}
          placeholder="Enter number only"
          label="Request ID"
          onChange={event => updateFilter(String(event.target.value), 'name')}
        />
      </div>
      {showDescriptionFilter && (
        <div className={classes.inputContainer}>
          <Select
            key="description"
            label="Description"
            InputLabelProps={{ style: styles.label }}
            fullWidth
            onChange={event => handleDescriptionUpdate(event)}
            value={descriptionValue}
          >
            {DESCRIPTION_OPTIONS.map((option: string) => (
              <MenuItem key={option} value={option}>
                {DESCRIPTION_LABELS[option]}
              </MenuItem>
            ))}
          </Select>
        </div>
      )}
      <div className={classes.inputContainer}>
        <UsersFilter
          value={filtersCart?.assignedTo}
          updateValue={newValue => updateFilter(newValue, 'createdBy')}
          placeholder="Search users"
          label="Created By"
          showTitle
        />
      </div>
      <div className={classes.inputContainer}>
        <InputDateRange
          label="Created Date"
          presetSelected={createdPresetSelected}
          startDateValue={getDateValue('createdDateStart')}
          handleStartChange={handleDateChange(
            'createdDateStart',
            DateRangeTerminator.START,
          )}
          endDateValue={getDateValue('createdDateEnd')}
          handleEndChange={handleDateChange(
            'createdDateEnd',
            DateRangeTerminator.END,
          )}
          handleSelectDatePreset={selected =>
            setCreatedPresetSelect(selected?.value)
          }
        />
      </div>
    </>
  );
};

export default SDDOTStockpileFilters;
